import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  MessageCircle,
  Send,
  RefreshCw,
  Heart,
  Clock,
  Bell,
  ExternalLink,
  Sparkles,
  Wifi,
  WifiOff
} from './icons/CustomDashboardIcons';
import { Check, CheckCheck, ArrowLeft, X } from './icons/ChatNavIcons';
import { toast } from 'sonner';
import { HeartAlchemyIcon, SpiralIcon } from './icons/AlchemyIcons';
import { useWebSocket, ConnectionState } from '../hooks/useWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// User avatar colors
const USER_COLORS = [
  'bg-amber-500', 'bg-rose-500', 'bg-teal-500', 'bg-violet-500',
  'bg-blue-500', 'bg-emerald-500', 'bg-orange-500', 'bg-pink-500'
];

function getUserColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function UserMessaging({ isOpen, onClose, initialConversationId = null }) {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);
  
  const userId = localStorage.getItem('workbook_user_id');
  const userName = localStorage.getItem('workbook_user_name');

  // WebSocket integration for real-time messages
  const handleNewWebSocketMessage = useCallback((data) => {
    console.log('[UserMessaging] WebSocket message received:', data);
    
    // Handle new message notification
    if (data.type === 'new_message') {
      // Refresh conversations to show new message
      fetchConversations();
      
      // If we're viewing the conversation this message belongs to, refresh messages
      if (activeConversation && data.conversation_id === activeConversation._id) {
        fetchMessages(activeConversation._id, true);
      }
      
      // Show toast notification
      toast.info(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">New message from {data.sender_name}</span>
          <span className="text-sm text-stone-400">{data.preview}</span>
        </div>,
        { duration: 4000 }
      );
    }
  }, [activeConversation]);

  // Initialize WebSocket (only when messaging panel is open)
  const { connectionState, isConnected } = useWebSocket(
    isOpen && userId ? { _id: userId, name: userName || 'User' } : null,
    {
      autoConnect: true,
      onNewMessage: handleNewWebSocketMessage
    }
  );

  // Fetch conversations
  useEffect(() => {
    if (isOpen && userId) {
      fetchConversations();
      fetchNotifications();
      
      // Only use polling as fallback when WebSocket is not connected
      if (!isConnected) {
        pollInterval.current = setInterval(() => {
          fetchConversations();
          fetchNotifications();
          if (activeConversation) {
            fetchMessages(activeConversation._id, true);
          }
        }, 5000); // Reduced polling frequency when WebSocket fallback
      }
      
      return () => {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };
    }
  }, [isOpen, userId, activeConversation?._id, isConnected]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Open initial conversation if provided
  useEffect(() => {
    if (initialConversationId && conversations.length > 0) {
      const conv = conversations.find(c => c._id === initialConversationId);
      if (conv) {
        openConversation(conv);
      }
    }
  }, [initialConversationId, conversations]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations`, {
        headers: {
          'X-User-Id': userId,
          'X-User-Name': userName
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications?unread_only=true`, {
        headers: { 'X-User-Id': userId }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchMessages = async (conversationId, polling = false) => {
    try {
      const since = polling && messages.length > 0 
        ? messages[messages.length - 1].created_at 
        : null;
      
      const url = `${BACKEND_URL}/api/conversations/${conversationId}/messages${since ? `?since=${since}` : ''}`;
      const response = await fetch(url, {
        headers: { 'X-User-Id': userId }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (polling && since) {
          setMessages(prev => [...prev, ...data.messages]);
        } else {
          setMessages(data.messages || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const openConversation = async (conversation) => {
    setActiveConversation(conversation);
    setMessages(conversation.messages || []);
    
    // Fetch full conversation to mark as read
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/${conversation._id}`, {
        headers: { 'X-User-Id': userId }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to open conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/conversations/${activeConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          content: newMessage.trim(),
          message_type: 'text'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'X-User-Id': userId }
      });
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const getOtherParticipant = (conversation) => {
    const participants = conversation.participants || [];
    return participants.find(p => p.user_id !== userId) || { user_name: 'Unknown' };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl h-[600px] p-0 gap-0 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-700">
          {activeConversation ? (
            <>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveConversation(null)}
                  className="text-stone-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className={`w-8 h-8 rounded-full ${getUserColor(getOtherParticipant(activeConversation).user_id)} flex items-center justify-center text-white font-medium`}>
                  {getOtherParticipant(activeConversation).user_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-medium text-white">{getOtherParticipant(activeConversation).user_name}</h3>
                  {activeConversation.type === 'empathy_session' && (
                    <Badge className="text-xs bg-rose-500/20 text-rose-400 border-rose-500/30">
                      <HeartAlchemyIcon className="w-3 h-3 mr-1" />
                      Empathy Session
                    </Badge>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-amber-400" />
              <h3 className="font-medium text-white">Messages</h3>
              {notifications.length > 0 && (
                <Badge className="bg-rose-500 text-white">{notifications.length}</Badge>
              )}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} className="text-stone-400 hover:text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        {activeConversation ? (
          // Chat View
          <div className="flex flex-col h-[calc(600px-130px)]">
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <HeartAlchemyIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Start the conversation with empathy and care 💚</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg._id || idx}
                      className={`flex ${msg.sender_id === userId ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] p-3 rounded-lg ${
                          msg.sender_id === userId
                            ? 'bg-amber-600/20 border border-amber-500/30 rounded-br-none'
                            : 'bg-stone-800 border border-stone-700 rounded-bl-none'
                        }`}
                      >
                        {msg.sender_id !== userId && (
                          <p className="text-xs text-teal-400 mb-1">{msg.sender_name}</p>
                        )}
                        <p className="text-sm text-stone-200">{msg.content}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-[10px] text-stone-500">{formatTime(msg.created_at)}</span>
                          {msg.sender_id === userId && (
                            msg.read ? (
                              <CheckCheck className="w-3 h-3 text-teal-400" />
                            ) : (
                              <Check className="w-3 h-3 text-stone-500" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="p-4 border-t border-stone-700">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message with empathy..."
                  className="bg-stone-800 border-stone-700 text-white flex-1"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Conversations List
          <ScrollArea className="h-[calc(600px-70px)]">
            {/* Notifications Section */}
            {notifications.length > 0 && (
              <div className="p-4 border-b border-stone-700">
                <h4 className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                  <Bell className="w-3 h-3" /> NEW NOTIFICATIONS
                </h4>
                <div className="space-y-2">
                  {notifications.slice(0, 3).map(notif => (
                    <div
                      key={notif._id}
                      className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg cursor-pointer hover:bg-rose-500/20 transition-colors"
                      onClick={() => {
                        markNotificationRead(notif._id);
                        if (notif.conversation_id) {
                          const conv = conversations.find(c => c._id === notif.conversation_id);
                          if (conv) openConversation(conv);
                        }
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-rose-500/20 flex items-center justify-center">
                          {notif.type === 'empathy_alert' ? (
                            <Heart className="w-3 h-3 text-rose-400" />
                          ) : notif.type === 'empathy_response' ? (
                            <HeartAlchemyIcon className="w-3 h-3 text-teal-400" />
                          ) : (
                            <MessageCircle className="w-3 h-3 text-amber-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-white">{notif.title}</p>
                          <p className="text-xs text-stone-400">{notif.message}</p>
                        </div>
                        <span className="text-[10px] text-stone-500">{formatTime(notif.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conversations */}
            <div className="p-4">
              <h4 className="text-xs text-stone-500 mb-2">CONVERSATIONS</h4>
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 text-stone-500 animate-spin mx-auto" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                  <p className="text-xs mt-1">Use the Empathy Alert to connect with others</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map(conv => {
                    const other = getOtherParticipant(conv);
                    const lastMessage = conv.messages?.[conv.messages.length - 1];
                    
                    return (
                      <div
                        key={conv._id}
                        onClick={() => openConversation(conv)}
                        className="p-3 bg-stone-800/50 border border-stone-700 rounded-lg cursor-pointer hover:bg-stone-800 hover:border-stone-600 transition-colors"
                        data-testid={`conversation-${conv._id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${getUserColor(other.user_id)} flex items-center justify-center text-white font-medium`}>
                            {other.user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-white">{other.user_name}</span>
                              {conv.unread_count > 0 && (
                                <Badge className="bg-amber-500 text-white text-xs">{conv.unread_count}</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {conv.type === 'empathy_session' && (
                                <HeartAlchemyIcon className="w-3 h-3 text-rose-400" />
                              )}
                              <p className="text-xs text-stone-400 truncate">
                                {lastMessage?.content || 'Start conversation...'}
                              </p>
                            </div>
                          </div>
                          <span className="text-[10px] text-stone-500">
                            {lastMessage ? formatTime(lastMessage.created_at) : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
